/**
 * ── A FRASE DO CADEADO DE ORDEM — UMA SÓ, PARA TODOS OS LEITORES ───────────
 *
 * O pré-requisito é decidido em `trancasPorPreRequisito`, ao lado, e a porta é
 * fechada por `exigirAula`. O que este arquivo faz é transformar esse fato em
 * duas coisas, e em mais nenhuma:
 *
 * 1. **a frase** — derivada, nunca escrita à mão nem pelo tutor (R5-bis,
 *    `docs/member-arquitetura.md` §15.8);
 * 2. **as medidas** — os dois números que a frase nomeia, para a régua desenhar
 *    a distância em vez de a pessoa a calcular.
 *
 * ## Por que no core, e não na tela
 *
 * Porque os leitores são SEIS: o cartão da vitrine, o cartão da trilha, a etapa
 * dentro da trilha, a página do curso, o app do telemóvel — e o **403 de
 * `exigirAula`**, que é o único que importa quando os outros cinco falham.
 * Redigida em cada um, a mesma porta teria seis redações, e a que mentisse seria
 * sempre a que ninguém está a olhar.
 *
 * Nasceu em `apps/aluno/src/lib/tranca.ts` na leva de 23/09/2026, porque o core
 * estava a ser editado por outra sessão e o trabalho foi dividido por ARQUIVO.
 * Mudou-se de casa no mesmo dia, com as cercas de lá a servirem de prova: elas
 * continuam a importar isto pelo mesmo nome e continuam verdes, o que é a
 * maneira de provar que a mudança de casa não trocou uma palavra.
 *
 * ## `null` não é zero, e esta é a linha que mais custa
 *
 * - `notaAtual === null` **não** é «tirou 0»: é *o exigido não tem avaliação
 *   publicada nenhuma*. Nesse caso a nota nem tranca — o servidor ignora a
 *   exigência, porque exigir nota de um curso sem prova é cadeado sem chave —,
 *   e a frase **não pode mencionar nota**. Quem nunca fez a prova de um curso
 *   que tem prova chega aqui com `0`, que é fato medido, e esse sim entra.
 * - `percentualAtual === null` é *não sei onde você está*, e aí não há régua:
 *   uma barra vazia a dizer 0% seria a tela a inventar um fato.
 */

/** O que pode ser exigido. `trilha` entrou em 23/09/2026, com a ordem do dono. */
export type EspecieExigida = 'curso' | 'modulo' | 'aula' | 'trilha';

/**
 * O motivo do cadeado, como o servidor o manda.
 *
 * Tudo o que é número vem `null` quando o contrato que respondeu é o antigo (o
 * de 19/09, que só mandava `titulo` e `faltam`). Nada aqui é estimado: campo
 * ausente vira `null` e some da frase, em vez de virar zero.
 */
export interface TrancaDeOrdem {
  exigeTipo: EspecieExigida;
  exigeId: string;
  /** O título do que falta. É o que a frase nomeia — e o que ela linka. */
  titulo: string;
  /** Quantas dependências faltam ao todo, contando esta. */
  faltam: number;
  /** 1..100. `100` = «conclua tudo». `null` = contrato antigo. */
  percentualMinimo: number | null;
  /** 0..100, onde a pessoa está. `null` = o motor não mediu. */
  percentualAtual: number | null;
  /** `null` = o tutor não exige nota. */
  notaMinima: number | null;
  /** `null` = o exigido NÃO TEM avaliação publicada. Nunca «tirou 0». */
  notaAtual: number | null;
  /**
   * A frase curta do contrato de 19/09, quando é ela que chega.
   *
   * Reserva, e só: com os fatos na mão a frase é derivada, porque só a derivada
   * consegue dizer «pede 60% — você está em 25%». Sem os fatos, a do servidor é
   * melhor do que qualquer coisa que esta tela inventasse.
   */
  frase?: string;
}

/** Um número do servidor, ou `null`. Nunca um palpite. */
function numero(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

const ESPECIES: readonly string[] = ['curso', 'modulo', 'aula', 'trilha'];

/**
 * Lê a tranca de um corpo JSON, tolerando o contrato antigo e o corpo torto.
 *
 * O leitor é tolerante de propósito: o app do aluno é servido por um `dist` que
 * pode ser mais novo que a API por alguns minutos (e mais velho por outros
 * tantos, quando a aba fica aberta). Um `undefined.percentualMinimo` no meio da
 * vitrine apagaria a prateleira inteira para dizer que um cadeado não tinha um
 * campo.
 */
export function lerTranca(bruto: unknown): TrancaDeOrdem | null {
  if (!bruto || typeof bruto !== 'object') return null;
  const t = bruto as Record<string, unknown>;
  if (typeof t.titulo !== 'string' || t.titulo === '') return null;

  const tipo =
    typeof t.exigeTipo === 'string' && ESPECIES.includes(t.exigeTipo) ? t.exigeTipo : 'curso';

  return {
    exigeTipo: tipo as EspecieExigida,
    exigeId: typeof t.exigeId === 'string' ? t.exigeId : '',
    titulo: t.titulo,
    faltam: numero(t.faltam) ?? 1,
    percentualMinimo: numero(t.percentualMinimo),
    percentualAtual: numero(t.percentualAtual),
    notaMinima: numero(t.notaMinima),
    notaAtual: numero(t.notaAtual),
    ...(typeof t.frase === 'string' ? { frase: t.frase } : {}),
  };
}

/** `0..100`, e para baixo: dizer 100% a quem ainda não terminou é mentira. */
function pontos(v: number): number {
  return Math.max(0, Math.min(100, Math.floor(v)));
}

/**
 * Uma exigência do tutor, com o lugar onde a pessoa está.
 *
 * `cumprida` existe para a régua poder mostrar as duas: quando o tutor pede 70%
 * **e** nota 70, ver o avanço já passado da marca é o que diz «isto é
 * alcançável, falta só a outra metade». Mostrar só a pendente deixaria a pessoa
 * sem saber se o resto conta.
 */
export interface MedidaDaTranca {
  rotulo: 'avanço' | 'nota';
  minimo: number;
  atual: number;
  cumprida: boolean;
}

export function medidasDaTranca(t: TrancaDeOrdem): MedidaDaTranca[] {
  const medidas: MedidaDaTranca[] = [];

  if (t.percentualMinimo !== null && t.percentualAtual !== null) {
    const minimo = pontos(t.percentualMinimo);
    const atual = pontos(t.percentualAtual);
    medidas.push({ rotulo: 'avanço', minimo, atual, cumprida: atual >= minimo });
  }

  /* `notaAtual === null` é «o exigido não tem prova», e aí a nota não tranca
     nada: régua nenhuma, número nenhum. Ver o cabeçalho. */
  if (t.notaMinima !== null && t.notaAtual !== null) {
    const minimo = pontos(t.notaMinima);
    const atual = pontos(t.notaAtual);
    medidas.push({ rotulo: 'nota', minimo, atual, cumprida: atual >= minimo });
  }

  return medidas;
}

/**
 * A frase em três pedaços, porque o título do que falta é **um link**.
 *
 * O aluno precisa de chegar ao que o destranca, e o nome dele já está escrito
 * na frase: repetir o nome num botão «Abrir X» ao lado seria dizer duas vezes a
 * mesma palavra a 8px de distância. O nome é a porta.
 *
 * `titulo: null` = não há nome a dizer (contrato sem tranca): a frase inteira
 * está em `antes`, e nada vira link.
 */
export interface FraseDaTranca {
  antes: string;
  titulo: string | null;
  depois: string;
}

const ARTIGO: Record<EspecieExigida, string> = {
  curso: 'o curso',
  modulo: 'o módulo',
  aula: 'a aula',
  trilha: 'a trilha',
};

/** «Depende de outro conteúdo desta escola.» — ver `frase` no leitor. */
export const SEM_MOTIVO = 'Depende de outro conteúdo desta escola.';

function maiuscula(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * ── AS CINCO LEITURAS ───────────────────────────────────────────────────────
 *
 * | fato | frase |
 * |---|---|
 * | pede tudo, e falta | `Conclua o curso «X» para abrir.` |
 * | pede 60%, você em 25% | `O curso «X» pede 60% — você está em 25%.` |
 * | pede nota 70, a sua 55 | `O curso «X» pede nota 70 — a sua é 55.` |
 * | os dois pendentes | `O curso «X» pede 60% e nota 70 — você está em 25% e tirou 55.` |
 * | `faltam > 1` | qualquer uma acima + `Há mais 2 pré-requisitos.` |
 *
 * ## Por que só o que está PENDENTE é nomeado
 *
 * Porque a frase é uma instrução, não um regulamento. Quem já está em 85% de um
 * curso que pede 70% não precisa de ler «pede 70%»: precisa de saber que o que
 * falta é a nota. A régua ao lado continua a mostrar as duas exigências, e é lá
 * que mora o quadro completo — a frase diz o próximo gesto.
 *
 * ## Por que `faltam` vira uma frase à parte
 *
 * «Conclua X» quando faltam três é verdadeiro e engana: a pessoa cumpre X,
 * volta, e encontra o cadeado no mesmo sítio sem perceber porquê. O número sai
 * numa segunda oração para caber depois de qualquer uma das quatro primeiras.
 */
export function partesDaFrase(t: TrancaDeOrdem): FraseDaTranca {
  const especie = ARTIGO[t.exigeTipo];
  const mais =
    t.faltam > 1
      ? ` Há mais ${t.faltam - 1} ${t.faltam - 1 === 1 ? 'pré-requisito' : 'pré-requisitos'}.`
      : '';

  /* Contrato antigo: sem os números, a frase do servidor é a melhor que existe,
     e esta tela não inventa uma maior. */
  if (t.percentualMinimo === null) {
    if (typeof t.frase === 'string' && t.frase !== '')
      return { antes: t.frase, titulo: null, depois: '' };
    return { antes: `Conclua ${especie} `, titulo: t.titulo, depois: ` para abrir.${mais}` };
  }

  const pedePercentual = t.percentualAtual === null || t.percentualAtual < t.percentualMinimo;
  const pedeNota = t.notaMinima !== null && t.notaAtual !== null && t.notaAtual < t.notaMinima;

  /* Nada pendente e ainda trancado: o corpo desta tela é mais velho do que o
     progresso, ou tranca outra régua. Dizer «conclua» seria mandar fazer o que
     já está feito. */
  if (!pedePercentual && !pedeNota) {
    return {
      antes: `Depende ${t.exigeTipo === 'aula' || t.exigeTipo === 'trilha' ? 'da' : 'do'} ${t.exigeTipo} `,
      titulo: t.titulo,
      depois: `.${mais}`,
    };
  }

  const exige: string[] = [];
  const voce: string[] = [];

  if (pedePercentual) {
    exige.push(t.percentualMinimo >= 100 ? 'conclusão' : `${pontos(t.percentualMinimo)}%`);
    if (t.percentualAtual !== null) voce.push(`está em ${pontos(t.percentualAtual)}%`);
  }
  if (pedeNota) {
    exige.push(`nota ${pontos(t.notaMinima!)}`);
    voce.push(`tirou ${pontos(t.notaAtual!)}`);
  }

  /* O caso simples fica com a frase simples: «Conclua X para abrir» é a que o
     aluno já lia em 19/09, e é melhor português do que «pede conclusão». */
  if (exige.length === 1 && exige[0] === 'conclusão') {
    return { antes: `Conclua ${especie} `, titulo: t.titulo, depois: ` para abrir.${mais}` };
  }

  /* «você tirou 55» sozinho soa a boletim; «a sua é 55» continua a falar da nota
     que a frase acabou de nomear. Com as duas exigências pendentes a forma tem
     de voltar a «você», porque aí há dois sujeitos na mesma oração. */
  const cauda =
    voce.length === 0
      ? ' para abrir.'
      : pedeNota && !pedePercentual
        ? ` — a sua é ${pontos(t.notaAtual!)}.`
        : ` — você ${voce.join(' e ')}.`;
  return {
    antes: `${maiuscula(especie)} `,
    titulo: t.titulo,
    depois: ` pede ${exige.join(' e ')}${cauda}${mais}`,
  };
}

/**
 * A frase inteira, numa cadeia — para o `aria-label`, para o 403 e para quem
 * não linka.
 *
 * **«DeOrdem» no nome** porque esta casa tem duas trancas com frase: esta, do
 * pré-requisito, e a de `tranca-por-avaliacao.ts`, que fecha os módulos
 * seguintes até a prova obrigatória ser passada. Chamar as duas `fraseDaTranca`
 * fazia o `index.ts` do core exportar a mesma palavra duas vezes — e, muito
 * antes disso, faria quem lê um `import` não saber de que porta se fala.
 */
export function fraseDaTrancaDeOrdem(t: TrancaDeOrdem): string {
  const p = partesDaFrase(t);
  return p.titulo === null ? p.antes : `${p.antes}«${p.titulo}»${p.depois}`;
}
